export const PHOTO_GEOMETRY_VERSION = 'everyid.photo-cover/1';
// Remove floating-point noise near integer pixels without quantizing fractional pans.
const stable = value => Math.abs(value - Math.round(value)) < 1e-9 ? Math.round(value) : value;

export function sourceCoverRect(sw, sh, fw, fh, crop = {}, reference = null) {
  if (![sw, sh, fw, fh].every(v => Number.isFinite(v) && v > 0)) throw new RangeError('Invalid source/frame dimensions');
  const zoom = Number.isFinite(crop.scale) ? Math.max(1, crop.scale) : 1;
  const scale = Math.max(fw / sw, fh / sh) * zoom;
  const width = fw / scale, height = fh / scale;
  let x = crop.x, y = crop.y;
  if (reference?.width > 0 && reference?.height > 0) {
    const rs = Math.max(reference.width / sw, reference.height / sh) * zoom;
    const rw = reference.width / rs, rh = reference.height / rs;
    const rx = Number.isFinite(x) ? Math.min(Math.max(0, sw - rw), Math.max(0, x)) : (sw - rw) / 2;
    const ry = Number.isFinite(y) ? Math.min(Math.max(0, sh - rh), Math.max(0, y)) : (sh - rh) / 2;
    x = rx + rw / 2 - width / 2; y = ry + rh / 2 - height / 2;
  }
  x = Number.isFinite(x) ? Math.min(Math.max(0, sw - width), Math.max(0, x)) : (sw - width) / 2;
  y = Number.isFinite(y) ? Math.min(Math.max(0, sh - height), Math.max(0, y)) : (sh - height) / 2;
  return { version: PHOTO_GEOMETRY_VERSION, x, y, width, height, scale, centerX: (x + width / 2) / sw, centerY: (y + height / 2) / sh };
}

// Source-pixel Cover adapter; paired with RenderWorker PhotoGeometry.Cover.
export const POSITION_VERSION = 'everyid.photo-position/1';
export const MAX_ZOOM = 8;
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const SIDES = { Front: 'objectsFront', Back: 'objectsBack', FrontSilver: 'objectsFrontSilver', BackSilver: 'objectsBackSilver' };
const isCaptureFrame = (card, frame, declaration) => {
  if (!frame || frame.type !== 'photo') return false;
  const binding = frame.dataBinding;
  const emptyBinding = !binding || !Object.keys(binding).length ||
    (Object.keys(binding).length === 1 && Array.isArray(binding.conditions) && !binding.conditions.length);
  const ownSide = Object.values(SIDES).map(side => card[side] || []).find(objects => objects.includes(frame));
  return typeof frame.id === 'string' && !!frame.id.trim() &&
    ownSide?.filter(object => object.id === frame.id).length === 1 &&
    !frame.containerId && !frame.textBinding && emptyBinding &&
    (declaration || !(frame.src || frame.originalSrc)) &&
    [frame.width, frame.height].every(value => Number.isFinite(value) && value > 0);
};
const firstCaptureFrame = (card, sides, declaration) => sides
  .flatMap(side => card[SIDES[side]] || [])
  .find(frame => isCaptureFrame(card, frame, declaration)) || null;
export function findPhotoFrame(card) {
  if (!card) return null;
  const declaration = card.capturePhotoBinding;
  if (declaration) {
    if (declaration.version !== 'everyid.photo-binding/1' || declaration.source !== 'media.photo') return null;
    const reference = declaration.referenceFrame || (declaration.frames?.length === 1 ? declaration.frames[0] : null);
    if (!reference || !SIDES[reference.side] || !declaration.frames?.some(f => f.side === reference.side && f.elementId === reference.elementId)) return null;
    const candidates = (card[SIDES[reference.side]] || [])
      .filter(object => object.id === reference.elementId && object.type === 'photo');
    if (candidates.length === 1) return isCaptureFrame(card, candidates[0], declaration) ? candidates[0] : null;
    if (candidates.length > 1) return null;
    // A deleted primary must not suppress Capture. Use the first valid front photo
    // in existing drawing order; secondary frames share this one captured source.
    return firstCaptureFrame(card, ['Front'], declaration);
  }
  return firstCaptureFrame(card, ['Front', 'Back', 'FrontSilver', 'BackSilver'], declaration);
}
export function isAttachmentOnly(card) {
  return !!card && !card.capturePhotoBinding && ['objects', ...Object.values(SIDES)].every(side => !(card[side] || []).some(o => o.type?.toLowerCase() === 'photo'));
}
export function getCropGeometry(width, height, aspect, naturalWidth, naturalHeight) {
  if (![width, height, aspect, naturalWidth, naturalHeight].every(n => Number.isFinite(n) && n > 0)) return null;
  const frameW = Math.min(width * .8, height * .8 * aspect), frameH = frameW / aspect;
  const frameX = (width - frameW) / 2, frameY = (height - frameH) / 2;
  const displayScale = Math.max(frameW / naturalWidth, frameH / naturalHeight);
  const maxX = Math.max(0, naturalWidth - frameW / displayScale), maxY = Math.max(0, naturalHeight - frameH / displayScale);
  return {frameW, frameH, frameX, frameY, displayScale, naturalWidth, naturalHeight, maxX, maxY, centerCrop: {x: maxX / 2, y: maxY / 2, scale: 1}};
}
export function clampSourceCrop(crop, g) {
  const scale = clamp(Number.isFinite(crop.scale) ? crop.scale : 1, 1, MAX_ZOOM);
  const rect = sourceCoverRect(g.naturalWidth, g.naturalHeight, g.frameW, g.frameH, {...crop, scale});
  return {x: rect.x, y: rect.y, scale};
}
export function zoomCrop(crop, scale, fx, fy, g) {
  scale = clamp(scale, 1, MAX_ZOOM);
  return clampSourceCrop({x: crop.x + fx * g.frameW / g.displayScale * (1 / crop.scale - 1 / scale), y: crop.y + fy * g.frameH / g.displayScale * (1 / crop.scale - 1 / scale), scale}, g);
}
export function saveCrop(crop, g) {
  const next = clampSourceCrop(crop, g), factor = g.displayScale * next.scale;
  return {...next, position: {version: POSITION_VERSION, centerX: (next.x + g.frameW / factor / 2) / g.naturalWidth, centerY: (next.y + g.frameH / factor / 2) / g.naturalHeight, zoom: next.scale}};
}
export function restoreCrop(position, g) {
  if (position?.version !== POSITION_VERSION) return g.centerCrop;
  return clampSourceCrop({x: position.centerX * g.naturalWidth - g.frameW / (g.displayScale * position.zoom) / 2, y: position.centerY * g.naturalHeight - g.frameH / (g.displayScale * position.zoom) / 2, scale: position.zoom}, g);
}
export function faceCrop(faces, g) {
  const valid = faces.filter(f => [f.x, f.y, f.width, f.height].every(Number.isFinite) && f.width > 0 && f.height > 0);
  const distance = f => Math.hypot((f.x + f.width / 2) / g.naturalWidth - .5, (f.y + f.height / 2) / g.naturalHeight - .5);
  valid.sort((a,b) => b.width * b.height - a.width * a.height || distance(a) - distance(b) || a.y - b.y || a.x - b.x);
  if (!valid.length) return g.centerCrop;
  const f = valid[0];
  // Face height ~40% of the frame; face center ~40% down, leaving headroom.
  const zoom = clamp(g.frameH / (f.height * g.displayScale * 2.5), 1, MAX_ZOOM);
  const factor = g.displayScale * zoom;
  return clampSourceCrop({x: f.x + f.width / 2 - g.frameW / factor / 2, y: f.y + f.height / 2 - g.frameH / factor * .4, scale: zoom}, g);
}

export function findCapturedPhotoFrame(card) { return card?.capturePhotoBinding ? findPhotoFrame(card) : null; }
